"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/inline-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@/lib/api-client";
import type { Availability } from "@/lib/api-client";
import { Trash2, Plus, CalendarRange } from "lucide-react";
import { formatDate } from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";

export function AvailabilityModal({
  productId,
  open,
  onOpenChange,
}: {
  productId: string | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["availability", productId],
    queryFn: () =>
      apiGet<{ availabilities: Availability[] }>(`/api/products/${productId}/availability`),
    enabled: !!productId && open,
  });

  const list = data?.availabilities ?? [];

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productId || !start || !end) return;
    setBusy(true);
    try {
      await apiPost(`/api/products/${productId}/availability`, {
        startDate: start,
        endDate: end,
      });
      toast.success(t("toast.availAdded"));
      setStart("");
      setEnd("");
      qc.invalidateQueries({ queryKey: ["availability", productId] });
      qc.invalidateQueries({ queryKey: ["product", productId] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotSave"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!productId) return;
    try {
      await apiDelete(`/api/products/${productId}/availability/${id}`);
      toast.success(t("toast.availRemoved"));
      qc.invalidateQueries({ queryKey: ["availability", productId] });
      qc.invalidateQueries({ queryKey: ["product", productId] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotRemove"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5" /> {t("avail.title")}
          </DialogTitle>
          <DialogDescription>{t("avail.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={add} className="grid gap-3 rounded-lg border p-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>{t("avail.from")}</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("avail.to")}</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} required />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="gap-2">
            <Plus className="h-4 w-4" /> {t("avail.addWindow")}
          </Button>
        </form>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold">{t("avail.current", { count: list.length })}</h4>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("avail.empty")}</p>
          ) : (
            <ul className="grid gap-2">
              {list.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 text-sm"
                >
                  <span>
                    {formatDate(a.startDate, locale)} — {formatDate(a.endDate, locale)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(a.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
